<script lang="ts" setup>
import { getMetaTags } from '~/utils/meta'
import type { ImportantToKnowItem } from '~/types/elementsUi'
import type { SchemaOrgStep } from '~/types/schema-org'
import { vegetableListSvgImages } from '~/models/images'
import { pdfs, videos } from '~/models/files'
import { heroImages } from '#shared/models/images'

definePageMeta({
  layout: false
})

defineRouteRules({
  prerender: true
})

interface StepsToFindCookItems {
  'step-one': {
    title: string
    content: string
  }
  'step-two': {
    title: string
    content: {
      main: string
    }
  }
  'step-three': {
    title: string
    content: string
  }
  'step-four': {
    title: string
    content: string
  }
}

const { t, tm, baseKey } = useI18n('pages.static.maaltijden')

const heroImage = heroImages['irene-en-rob']

const nikkiVideoMp4 = videos.nikki

const pdfLink1 = pdfs.thuiskok['sociale-omgangsregels']
const pdfLink2 = pdfs.thuiskok['hygiene-regels']

useSeoMeta(
  getMetaTags(
    {
      title: '.meta.title',
      description: '.meta.description'
    },
    heroImage.src,
    baseKey
  )
)

const stepsToFindCook = computed<SchemaOrgStep[]>(() => {
  const rawSteps: StepsToFindCookItems = tm('.section-five.items')

  const steps = [
    {
      title: rawSteps['step-one'].title,
      content: rawSteps['step-one'].content
    },
    {
      title: rawSteps['step-two'].title,
      content: rawSteps['step-two'].content.main
    },
    {
      title: rawSteps['step-three'].title,
      content: rawSteps['step-three'].content
    },
    {
      title: rawSteps['step-four'].title,
      content: rawSteps['step-four'].content
    }
  ]

  return steps.map((step, index) => {
    return {
      url: `https://thuisgekookt.nl/maaltijden#step-${index + 1}`,
      name: step.title ?? '',
      text: step.content ?? '',
      image: vegetableListSvgImages[index]
    }
  })
})

useSchemaOrg(
  getSchemaOrg(
    {
      title: '.title',
      breadcrumbs: [{ name: '.title' }],
      howTo: {
        name: '.title',
        step: stepsToFindCook.value
      }
    },
    baseKey
  )
)

const importantToKnowItems = computed<ImportantToKnowItem[]>(() =>
  tm('.section-two.items')
)

const registrationStore = useRegistrationStore()

async function goToSignUpPage() {
  registrationStore.setIsPraatjePot()

  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event: 'clickEvents',
    params: {
      category: 'Klik naar aanmeldpagina',
      action: 'Aanmelden voor een maaltijd',
      label: ''
    }
  })
}
</script>

<template>
  <div>
    <NuxtLayout
      name="default"
      title=".hero.title"
      description=".hero.description"
      :image="heroImage"
      :ui="{
        image: 'lg:object-[center_-3rem] dark:object-[-19rem_top] dark:md:object-center'
      }"
    >
      <template #buttons>
        <VButton
          :to="{ name: 'registreren-maaltijdzoeker' }"
          label="buttons.foodie"
          @click="goToSignUpPage"
        />
      </template>

      <VSection title=".section-one.title" description=".section-one.description" />

      <ImportantToKnowSection title=".section-two.title" :items="importantToKnowItems" />

      <ExperiencesOfOtherFoodiesSection
        :description="`${baseKey}.section-three.description`"
      />

      <QuoteSection type="quote" title=".section-four.quote" />

      <VSection>
        <template #content>
          <div class="flex justify-center">
            <VVideo
              :videos="{ src: nikkiVideoMp4, type: 'video/mp4' }"
              class="rounded-2xl"
              width="320"
            />
          </div>

          <div class="section-button">
            <VButton
              :to="{ name: 'registreren-maaltijdzoeker' }"
              label="buttons.foodie"
              @click="goToSignUpPage"
            />
          </div>
        </template>
      </VSection>

      <VSection title=".section-five.title">
        <ol>
          <li>
            <h3 class="paragraph-title">
              {{ t('.section-five.items.step-one.title') }}
            </h3>
            <p>
              {{ t('.section-five.items.step-one.content') }}
            </p>
          </li>
          <li>
            <h3 class="paragraph-title">
              {{ t('.section-five.items.step-two.title') }}
            </h3>
            <p class="mb-0">
              {{ t('.section-five.items.step-two.content.main') }}
            </p>
            <ul>
              <li>
                <VI18n
                  tag="span"
                  class="font-bold"
                  key-path=".section-five.items.step-two.content.focus.question"
                >
                  <template #link>
                    <VLink
                      :to="{
                        name: 'index',
                        hash: '#focusgemeentes'
                      }"
                      label=".section-five.items.step-two.content.focus.link"
                    />
                  </template>
                </VI18n>
                {{ t('.section-five.items.step-two.content.focus.answer') }}
              </li>
              <li>
                <span class="font-bold">
                  {{ t('.section-five.items.step-two.content.non-focus.question') }}
                </span>
                {{ t('.section-five.items.step-two.content.non-focus.answer') }}
              </li>
            </ul>
          </li>
          <li>
            <h3 class="paragraph-title">
              {{ t('.section-five.items.step-three.title') }}
            </h3>
            <p>
              {{ t('.section-five.items.step-three.content') }}
            </p>
          </li>
          <li>
            <h3 class="paragraph-title">
              {{ t('.section-five.items.step-four.title') }}
            </h3>
            <p>
              {{ t('.section-five.items.step-four.content') }}
            </p>
          </li>
        </ol>
      </VSection>

      <VSection title=".section-six.title">
        <VI18n key-path=".section-six.content.paragraph-one">
          <template #link1>
            <VLink
              download
              :href="pdfLink1"
              target="_blank"
              label=".section-six.content.link-one"
            />
          </template>
          <template #link2>
            <VLink
              download
              :href="pdfLink2"
              target="_blank"
              label=".section-six.content.link-two"
            />
          </template>
        </VI18n>
        <p>
          {{ t('.section-six.content.paragraph-two') }}
        </p>
      </VSection>

      <TwoColorSection
        title=".section-seven.title"
        :ui="{
          from: 'from-pink',
          to: 'to-beige-800',
          mobile: 'max-lg:bg-pink',
          darkTo: 'dark:to-pure-black-700'
        }"
        icon="fa-hand-holding-heart"
        icon-color="dark-green-400"
        icon-dark-color="dark-green"
      >
        <p class="dark:text-pink-text">
          {{ t('.section-seven.content') }}
        </p>
      </TwoColorSection>

      <LocationsSection
        id="focus-municipalities"
        title="titles.our-focus-municipalities"
        description="descriptions.locations-section"
        page-exists
      >
        <template #after>
          <div class="section-button">
            <VButton
              :to="{ name: 'registreren-maaltijdzoeker' }"
              label="buttons.foodie"
              @click="goToSignUpPage"
            />
          </div>
        </template>
      </LocationsSection>

      <FAQSection />

      <VSection base-key=".section-ten" description=".description">
        <template #after>
          <div class="section-button">
            <VButton label="buttons.cook" :to="{ name: 'thuiskok' }" />
          </div>
        </template>
      </VSection>
    </NuxtLayout>
  </div>
</template>

<style scoped></style>
